import { Helmet } from "react-helmet-async";
import { useLanguage, LANGUAGES } from "@/i18n/translations";
import { SEO_DATA, DEFAULT_SEO } from "@/i18n/seoData";

const PROD_ORIGIN = "https://ayapay.ch";

/**
 * SEO — injects per-page localized meta tags (title, description, OG, Twitter, hreflang, canonical).
 * Canonical and hreflang always use the production domain (ayapay.ch) regardless of preview env.
 * Usage: <SEO page="home" />
 */
export default function SEO({ page = "home", image }) {
  const { lang } = useLanguage();
  const data = SEO_DATA[lang]?.[page] || SEO_DATA.fr[page] || {};
  const title = data.title || "AyaPay";
  const description = data.description || "";
  const ogImage = image || DEFAULT_SEO.ogImage;
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const canonical = `${PROD_ORIGIN}${path}`;

  const localeMap = { fr: "fr_CH", de: "de_CH", it: "it_CH", pt: "pt_PT", tr: "tr_TR", en: "en_US" };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta name="googlebot" content="index, follow" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />
      <meta property="og:locale" content={localeMap[lang] || "en_US"} />
      {LANGUAGES.filter((l) => l.code !== lang).map((l) => (
        <meta key={l.code} property="og:locale:alternate" content={localeMap[l.code]} />
      ))}
      <meta name="twitter:card" content={DEFAULT_SEO.twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <link rel="canonical" href={canonical} />
      {LANGUAGES.map((l) => (
        <link key={l.code} rel="alternate" hrefLang={l.code} href={canonical} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical} />
    </Helmet>
  );
}
